import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import {
	Resource,
	ResourceDetail,
	ResourceContent,
	ResourceListResponse,
	MyResourcesResponse,
	ResourceAccessRecord,
	CreateResourcePayload,
	UpdateResourcePayload,
	SortOrderUpdate,
	ProgressUpdate,
	ResourceCategory,
	ResourceAccessTier,
	ResourceContentType
} from '../../types/resourceHub';
import { AppDispatch, RootState } from '../store';
import { RequestStatusObject, createRequestStatusObject, buildReducers } from '../utils';

export interface ResourceHubState {
	resources: Resource[];
	resourcesNextToken: string | null;
	entitledSlugs: string[];
	selectedResource: ResourceDetail | null;
	resourceContent: ResourceContent | null;
	myResources: ResourceAccessRecord[];
	myResourcesNextToken: string | null;

	// Admin
	adminResources: Resource[];
	adminResourcesNextToken: string | null;

	// Request statuses
	resourcesRequestStatus: RequestStatusObject;
	resourceDetailRequestStatus: RequestStatusObject;
	resourceContentRequestStatus: RequestStatusObject;
	myResourcesRequestStatus: RequestStatusObject;
	createResourceRequestStatus: RequestStatusObject;
	updateResourceRequestStatus: RequestStatusObject;
	deleteResourceRequestStatus: RequestStatusObject;
	progressRequestStatus: RequestStatusObject;
	adminResourcesRequestStatus: RequestStatusObject;
}

const initialState: ResourceHubState = {
	resources: [],
	resourcesNextToken: null,
	entitledSlugs: [],
	selectedResource: null,
	resourceContent: null,
	myResources: [],
	myResourcesNextToken: null,

	// Admin
	adminResources: [],
	adminResourcesNextToken: null,

	// Request statuses
	resourcesRequestStatus: createRequestStatusObject(),
	resourceDetailRequestStatus: createRequestStatusObject(),
	resourceContentRequestStatus: createRequestStatusObject(),
	myResourcesRequestStatus: createRequestStatusObject(),
	createResourceRequestStatus: createRequestStatusObject(),
	updateResourceRequestStatus: createRequestStatusObject(),
	deleteResourceRequestStatus: createRequestStatusObject(),
	progressRequestStatus: createRequestStatusObject(),
	adminResourcesRequestStatus: createRequestStatusObject()
};

// ============================================================
// USER THUNKS
// ============================================================

export const fetchResources = createAsyncThunk<
	ResourceListResponse,
	{
		category?: ResourceCategory;
		accessTier?: ResourceAccessTier;
		contentType?: ResourceContentType;
		search?: string;
		nextToken?: string;
		limit?: number;
	},
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/fetchResources', async ({ category, accessTier, contentType, search, nextToken, limit }) => {
	const response = await API.get('inspirusapi', '/resources', {
		headers: {},
		response: true,
		queryStringParameters: {
			...(category && { category }),
			...(accessTier && { accessTier }),
			...(contentType && { contentType }),
			...(search && { search }),
			...(nextToken && { nextToken }),
			...(limit && { limit: String(limit) })
		}
	});
	return response.data;
});

export const fetchResourceDetail = createAsyncThunk<
	ResourceDetail,
	{ resourceId: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/fetchResourceDetail', async ({ resourceId }) => {
	const response = await API.get('inspirusapi', `/resources/${resourceId}`, {
		headers: {},
		response: true
	});
	return response.data;
});

export const fetchResourceContent = createAsyncThunk<
	ResourceContent,
	{ resourceId: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/fetchResourceContent', async ({ resourceId }) => {
	const response = await API.get('inspirusapi', `/resources/${resourceId}/content`, {
		headers: {},
		response: true
	});
	return response.data;
});

export const fetchMyResources = createAsyncThunk<
	MyResourcesResponse,
	{ nextToken?: string; limit?: number },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/fetchMyResources', async ({ nextToken, limit }) => {
	const response = await API.get('inspirusapi', '/resources/my-resources', {
		headers: {},
		response: true,
		queryStringParameters: {
			...(nextToken && { nextToken }),
			...(limit && { limit: String(limit) })
		}
	});
	return response.data;
});

export const purchaseResource = createAsyncThunk<
	{ checkoutUrl: string },
	{ resourceId: string; currency?: string; returnUrl?: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/purchaseResource', async ({ resourceId, currency, returnUrl }) => {
	const response = await API.post('inspirusapi', `/resources/${resourceId}/purchase`, {
		headers: {},
		response: true,
		body: {
			currency: currency || 'gbp',
			returnUrl: returnUrl || window.location.origin
		}
	});
	return response.data;
});

export const registerForWebinar = createAsyncThunk<
	{ registered: boolean },
	{ resourceId: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/registerForWebinar', async ({ resourceId }) => {
	const response = await API.post('inspirusapi', `/resources/${resourceId}/register`, {
		headers: {},
		response: true,
		body: {}
	});
	return response.data;
});

export const updateResourceProgress = createAsyncThunk<
	{ updated: boolean },
	{ resourceId: string; progress: ProgressUpdate },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/updateResourceProgress', async ({ resourceId, progress }) => {
	const response = await API.put('inspirusapi', `/resources/${resourceId}/progress`, {
		headers: {},
		response: true,
		body: progress
	});
	return response.data;
});

// ============================================================
// ADMIN THUNKS
// ============================================================

export const fetchAdminResources = createAsyncThunk<
	ResourceListResponse,
	{ nextToken?: string; limit?: number },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/fetchAdminResources', async ({ nextToken, limit }) => {
	const response = await API.get('inspirusapi', '/resources/admin/list', {
		headers: {},
		response: true,
		queryStringParameters: {
			...(nextToken && { nextToken }),
			...(limit && { limit: String(limit) })
		}
	});
	return response.data;
});

export const createResource = createAsyncThunk<
	{ resource: Resource },
	CreateResourcePayload,
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/createResource', async (payload) => {
	const response = await API.post('inspirusapi', '/resources', {
		headers: {},
		response: true,
		body: payload
	});
	return response.data;
});

export const updateResource = createAsyncThunk<
	{ resource: Resource },
	{ resourceId: string; payload: UpdateResourcePayload },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/updateResource', async ({ resourceId, payload }) => {
	const response = await API.put('inspirusapi', `/resources/${resourceId}`, {
		headers: {},
		response: true,
		body: payload
	});
	return response.data;
});

export const deleteResource = createAsyncThunk<
	{ deleted: boolean },
	{ resourceId: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/deleteResource', async ({ resourceId }) => {
	const response = await API.del('inspirusapi', `/resources/${resourceId}`, {
		headers: {},
		response: true
	});
	return response.data;
});

export const toggleResourcePublish = createAsyncThunk<
	{ visible: boolean },
	{ resourceId: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/toggleResourcePublish', async ({ resourceId }) => {
	const response = await API.put('inspirusapi', `/resources/${resourceId}/publish`, {
		headers: {},
		response: true,
		body: {}
	});
	return response.data;
});

export const updateResourceSortOrder = createAsyncThunk<
	{ updated: boolean },
	{ sortUpdates: SortOrderUpdate[] },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/updateResourceSortOrder', async ({ sortUpdates }) => {
	const response = await API.put('inspirusapi', '/resources/sort-order', {
		headers: {},
		response: true,
		body: { sortUpdates }
	});
	return response.data;
});

// ============================================================
// FILE UPLOAD THUNKS
// ============================================================

export const initiateMultipartUpload = createAsyncThunk<
	{
		uploadId: string;
		fileKey: string;
		partSize: number;
		totalParts: number;
		partUrls: { partNumber: number; url: string }[];
	},
	{ fileName: string; contentType: string; resourceType: string; fileSize: number },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/initiateMultipartUpload', async ({ fileName, contentType, resourceType, fileSize }) => {
	const response = await API.post('inspirusapi', '/resources/multipart/initiate', {
		headers: {},
		response: true,
		body: { fileName, contentType, resourceType, fileSize }
	});
	return response.data;
});

export const getMultipartUploadUrls = createAsyncThunk<
	{ partUrls: { partNumber: number; url: string }[] },
	{ fileKey: string; uploadId: string; partNumbers: number[] },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/getMultipartUploadUrls', async ({ fileKey, uploadId, partNumbers }) => {
	const response = await API.post('inspirusapi', '/resources/multipart/get-urls', {
		headers: {},
		response: true,
		body: { fileKey, uploadId, partNumbers }
	});
	return response.data;
});

export const completeMultipartUpload = createAsyncThunk<
	void,
	{ fileKey: string; uploadId: string; parts: { PartNumber: number; ETag: string }[] },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/completeMultipartUpload', async ({ fileKey, uploadId, parts }) => {
	const response = await API.post('inspirusapi', '/resources/multipart/complete', {
		headers: {},
		response: true,
		body: { fileKey, uploadId, parts }
	});
	return response.data;
});

export const getUploadUrl = createAsyncThunk<
	{ uploadUrl: string; fileKey: string },
	{ fileName: string; contentType: string; resourceType: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/getUploadUrl', async ({ fileName, contentType, resourceType }) => {
	const response = await API.post('inspirusapi', '/resources/upload-url', {
		headers: {},
		response: true,
		body: { fileName, contentType, resourceType }
	});
	return response.data;
});

// ============================================================
// CAPTION THUNKS
// ============================================================

export const fetchCaptionContent = createAsyncThunk<
	{ content: string },
	{ resourceId: string; lang: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/fetchCaptionContent', async ({ resourceId, lang }) => {
	const response = await API.get('inspirusapi', `/resources/${resourceId}/captions/${lang}`, {
		headers: {},
		response: true
	});
	return response.data;
});

export const saveCaptionContent = createAsyncThunk<
	void,
	{ resourceId: string; lang: string; content: string; label: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/saveCaptionContent', async ({ resourceId, lang, content, label }) => {
	const response = await API.put('inspirusapi', `/resources/${resourceId}/captions/${lang}`, {
		headers: {},
		response: true,
		body: { content, label }
	});
	return response.data;
});

export const deleteCaptionTrack = createAsyncThunk<
	void,
	{ resourceId: string; lang: string },
	{ dispatch: AppDispatch; state: RootState }
>('resourceHub/deleteCaptionTrack', async ({ resourceId, lang }) => {
	const response = await API.del('inspirusapi', `/resources/${resourceId}/captions/${lang}`, {
		headers: {},
		response: true
	});
	return response.data;
});

// ============================================================
// SLICE
// ============================================================

const resourceHubSlice = createSlice({
	name: 'resourceHub',
	initialState,
	reducers: {
		resetResourceHub: () => initialState,
		clearSelectedResource: (state) => {
			state.selectedResource = null;
			state.resourceContent = null;
		}
	},
	extraReducers: (builder) => {
		// fetchResources
		buildReducers(builder, fetchResources, 'resourcesRequestStatus', {
			onFulfilled: (state, payload) => {
				if (payload.nextToken && state.resources.length > 0) {
					// Append for pagination
					state.resources = [...state.resources, ...payload.items];
				} else {
					state.resources = payload.items;
				}
				state.resourcesNextToken = payload.nextToken;
				if (payload.entitledSlugs) {
					state.entitledSlugs = payload.entitledSlugs;
				}
			}
		});

		// fetchResourceDetail
		buildReducers(builder, fetchResourceDetail, 'resourceDetailRequestStatus', {
			onFulfilled: (state, payload) => {
				state.selectedResource = payload;
			}
		});

		// fetchResourceContent
		buildReducers(builder, fetchResourceContent, 'resourceContentRequestStatus', {
			onFulfilled: (state, payload) => {
				state.resourceContent = payload;
			}
		});

		// fetchMyResources
		buildReducers(builder, fetchMyResources, 'myResourcesRequestStatus', {
			onFulfilled: (state, payload) => {
				if (payload.nextToken && state.myResources.length > 0) {
					state.myResources = [...state.myResources, ...payload.items];
				} else {
					state.myResources = payload.items;
				}
				state.myResourcesNextToken = payload.nextToken;
			}
		});

		// updateResourceProgress
		buildReducers(builder, updateResourceProgress, 'progressRequestStatus');

		// Admin: fetchAdminResources
		buildReducers(builder, fetchAdminResources, 'adminResourcesRequestStatus', {
			onFulfilled: (state, payload) => {
				if (payload.nextToken && state.adminResources.length > 0) {
					state.adminResources = [...state.adminResources, ...payload.items];
				} else {
					state.adminResources = payload.items;
				}
				state.adminResourcesNextToken = payload.nextToken;
			}
		});

		// Admin: createResource
		buildReducers(builder, createResource, 'createResourceRequestStatus', {
			onFulfilled: (state, payload) => {
				state.adminResources = [payload.resource, ...state.adminResources];
			}
		});

		// Admin: updateResource
		buildReducers(builder, updateResource, 'updateResourceRequestStatus', {
			onFulfilled: (state, payload) => {
				const index = state.adminResources.findIndex(
					(r: Resource) => r.resourceId === payload.resource.resourceId
				);
				if (index >= 0) {
					state.adminResources[index] = payload.resource;
				}
			}
		});

		// Admin: deleteResource
		buildReducers(builder, deleteResource, 'deleteResourceRequestStatus');
	}
});

export const { resetResourceHub, clearSelectedResource } = resourceHubSlice.actions;
export default resourceHubSlice.reducer;
